import { useMemo } from 'react';
import type { Call_V2, Contact } from 'src/api/generated';
import { useContacts } from 'hooks/api/contacts/contacts';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';

/**
 * Response object from the useCallContacts hook.
 * Contains fetched contacts, a map for quick lookup by ID, and loading/error states.
 */
interface UseCallContactsResponse {
  /** Array of Contact objects fetched for the calls */
  contacts: Contact[];
  /** Map for O(1) contact lookup by ID */
  contactsMap: Map<number, Contact>;
  /** Whether contacts are currently being fetched */
  loading: boolean;
  /** Error from contacts fetch, if any */
  error: Error | null;
}

/**
 * Hook to fetch and map contacts associated with a list of calls.
 * Extracts unique contact IDs from calls and fetches corresponding contact data.
 *
 * @param calls - Array of Call_V2 objects.
 * @param enabled - Whether related contacts may be requested.
 * @returns Object containing contacts array, a Map for quick lookup, loading state, and error
 */
export const useCallContacts = (
  calls: Call_V2[],
  enabled = true
): UseCallContactsResponse => {
  const contactIds = useMemo(() => {
    const ids = calls.map((call) => call.contactId);
    return Array.from(new Set(ids));
  }, [calls]);

  const contactQuery = useMemo(() => {
    if (contactIds.length === 0) {
      return createQuery().build();
    }
    return createQuery().equals('id', contactIds).build();
  }, [contactIds]);

  const { contacts, loading, error } = useContacts(
    contactQuery,
    enabled && contactIds.length > 0
  );
  const visibleContacts = useMemo(
    () => (enabled && contactIds.length > 0 ? contacts : []),
    [contactIds.length, contacts, enabled]
  );

  const contactsMap = useMemo(() => {
    return new Map(visibleContacts.map((contact) => [contact.id, contact]));
  }, [visibleContacts]);

  return {
    contacts: visibleContacts,
    contactsMap,
    loading: enabled && contactIds.length > 0 && loading,
    error: enabled && contactIds.length > 0 ? error : null,
  };
};
